import {
  HttpClient,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';
import { API } from '../constants/APIConstants';

export interface PresignedUrlResponse {
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class ImageUploadService {
  private readonly http = inject(HttpClient);

  // ─── Observable API (preferred) ────────────────────

  getPresignedUrl$(fileName: string, moduleName: string, typeName: string): Observable<PresignedUrlResponse[]> {
    return this.http.post<PresignedUrlResponse[]>(API.GET_SIGNED_URL, {
      name: moduleName,
      type: typeName,
      files: [fileName],
    });
  }

  /**
   * Uploads raw bytes straight to S3. `contentType` defaults to JPEG for the
   * existing shop/fnb callers; pass the real type (e.g. `image/png`) so the stored
   * object is not mislabelled.
   */
  uploadToS3$(presignedUrl: string, file: Blob, contentType = 'image/jpeg'): Observable<void> {
    const headers = new HttpHeaders({ 'Content-Type': contentType });
    return this.http.put<void>(presignedUrl, file, { headers });
  }

  removeObject$(moduleName: string, imgUrl: string): Observable<unknown> {
    const path = imgUrl.split('cloudfront.net/')[1];
    if (!path) throw new Error('Invalid image URL');
    const headers = new HttpHeaders({ 'Content-Type': 'image/jpeg' });
    return this.http.post(API.REMOVE_STORAGE_OBJECT, { name: moduleName, path }, { headers });
  }

  // ─── Promise wrappers (backward compat for shop) ──

  /** @deprecated Use getPresignedUrl$() instead */
  getPresignedUrl(imageUrl: string, moduleName: string, typeName: string): Promise<PresignedUrlResponse[]> {
    return lastValueFrom(this.getPresignedUrl$(imageUrl, moduleName, typeName));
  }

  /** @deprecated Use uploadToS3$() instead */
  uploadImageToS3(presignedUrl: string, imageFile: Blob): Promise<void> {
    return lastValueFrom(this.uploadToS3$(presignedUrl, imageFile));
  }

  /** @deprecated Use removeObject$() instead */
  removeStorageObject(moduleName: string, imgUrl: string): Promise<unknown> {
    return lastValueFrom(this.removeObject$(moduleName, imgUrl));
  }
}
